<script setup lang="ts">
  import { computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Select, type SelectOption } from "@/components/episto-ui/select";
  import { Input } from "@/components/ui/input";
  import { Button } from "@/components/ui/button";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { FormDialog, FormField } from "@/components/episto-ui/form-dialog";
  import { Building2, CornerDownRight, FileText, Plus, Trash2 } from "lucide-vue-next";
  import moment from "moment";
  import { epistoUserTypes, userType } from "@utils/constants/userRoles";
  import { useAccountsStore } from "@stores/accounts";
  import { useSurveyOptions } from "@utils/composables/filters/useSurveyOptions";
  import type { User, Role, RoleGrantPayload, Account } from "@/types";

  const open = defineModel<boolean>("open", { required: true });
  const user = defineModel<Partial<User>>("user", { required: true });
  const roleGrants = defineModel<RoleGrantPayload[]>("roleGrants", { required: true });

  const props = defineProps<{
    isEditing?: boolean;
    roles: Role[];
    canEditUserType?: boolean;
  }>();

  const emit = defineEmits<{ save: [] }>();

  const { accounts } = storeToRefs(useAccountsStore());

  const { bindings: surveyPageBindings, load: loadSurveyOptions } = useSurveyOptions();

  const surveyOptionsBindings = computed(() => {
    const bindings = surveyPageBindings.value;
    const known = new Set(bindings.options.map((o) => String(o.value)));
    const grantOptions = (user.value.user_roles ?? [])
      .filter((grant) => grant.roleable_type === "Survey" && !known.has(String(grant.roleable_id)))
      .map((grant) => ({ label: grant.roleable_name, value: grant.roleable_id }));
    return grantOptions.length ? { ...bindings, options: [...grantOptions, ...bindings.options] } : bindings;
  });

  // Sentinel because the Select cannot hold null as an item value
  const NO_TYPE = "no_type";

  const userTypeOptions = [
    { label: "Super Admin", value: userType.SUPER_ADMIN },
    { label: "Admin", value: userType.ADMIN },
    { label: "Sales", value: userType.SALES },
    { label: "Client", value: userType.CLIENT },
    { label: "No type", value: NO_TYPE },
  ];

  const selectedUserType = computed<string>({
    get: () => user.value.user_type ?? NO_TYPE,
    set: (value) => {
      user.value.user_type = value === NO_TYPE ? null : (value as User["user_type"]);
    },
  });

  const nullableField = (key: "crm_id" | "hs_job_title" | "hs_phone") =>
    computed<string | number>({
      get: () => user.value[key] ?? "",
      set: (value) => (user.value[key] = value === "" ? null : String(value)),
    });
  const crmId = nullableField("crm_id");
  const hsJobTitle = nullableField("hs_job_title");
  const hsPhone = nullableField("hs_phone");

  const roleOptions = computed<SelectOption[]>(() => props.roles.map(({ id, name }) => ({ label: name, value: id })));

  // A sub-account shows as "Parent · Child" so the hierarchy stays readable
  const accountOptions = computed<SelectOption[]>(() =>
    accounts.value.map((account: Account) => {
      const parent = accounts.value.find((candidate: Account) => candidate.id === account.master_account_id);
      return { label: parent ? `${parent.name} · ${account.name}` : account.name, value: account.id };
    }),
  );

  const subAccountNames = (accountId: number) =>
    accounts.value
      .filter((account: Account) => account.master_account_id === accountId)
      .map((account: Account) => account.name);

  const accountGrants = computed(() => roleGrants.value.filter((grant) => grant.roleable_type === "Account"));
  const surveyGrants = computed(() => roleGrants.value.filter((grant) => grant.roleable_type === "Survey"));

  const addGrant = (roleableType: "Account" | "Survey") => {
    roleGrants.value = [...roleGrants.value, { role_id: 0, roleable_type: roleableType, roleable_id: 0 }];
  };

  const removeGrant = (grant: RoleGrantPayload) => {
    roleGrants.value = roleGrants.value.filter((entry) => entry !== grant);
  };

  const updateGrant = (grant: RoleGrantPayload, changes: Partial<RoleGrantPayload>) => {
    roleGrants.value = roleGrants.value.map((entry) => (entry === grant ? { ...entry, ...changes } : entry));
  };

  watch(open, (isOpen) => {
    if (isOpen) loadSurveyOptions(1);
  });

  const formatDate = (date: string | null | undefined): string => {
    if (!date) return "-";
    return moment(date).format("MMMM Do YYYY, h:mm:ss a");
  };

  const showHubspotFields = computed(() => epistoUserTypes.includes(user.value.user_type ?? ""));
  const roleAssignableUserTypes = [userType.CLIENT, userType.SALES];
  const showRoleSections = computed(() => roleAssignableUserTypes.includes(user.value.user_type ?? ""));
  const rolesGrantAccess = computed(() => user.value.user_type === userType.CLIENT);

  const canSave = computed(
    () => (user.value.email ?? "").includes("@") && (user.value.first_name ?? "").trim().length > 0,
  );
</script>

<template>
  <FormDialog
    v-model:open="open"
    :is-editing="isEditing"
    :can-save="canSave"
    entity-name="User"
    max-width="640px"
    @save="emit('save')">
    <div class="grid grid-cols-2 gap-4">
      <FormField
        id="first_name"
        label="First name">
        <Input
          id="first_name"
          v-model="user.first_name"
          placeholder="First name"
          class="text-sm" />
      </FormField>

      <FormField
        id="last_name"
        label="Last name">
        <Input
          id="last_name"
          v-model="user.last_name"
          placeholder="Last name"
          class="text-sm" />
      </FormField>
    </div>

    <FormField
      id="email"
      label="Email">
      <Input
        id="email"
        v-model="user.email"
        type="email"
        placeholder="email@company.com"
        class="text-sm" />
    </FormField>

    <FormField
      v-if="canEditUserType"
      id="user_type"
      label="Type">
      <Select
        v-model="selectedUserType"
        :options="userTypeOptions" />
    </FormField>

    <template v-if="showHubspotFields">
      <FormField
        id="crm_id"
        label="CRM ID">
        <Input
          id="crm_id"
          v-model="crmId"
          type="number"
          placeholder="HubSpot Contact ID"
          class="text-sm" />
      </FormField>

      <div class="grid grid-cols-2 gap-4">
        <FormField
          id="hs_job_title"
          label="Job Title">
          <Input
            id="hs_job_title"
            v-model="hsJobTitle"
            placeholder="CEO, Manager..."
            class="text-sm" />
        </FormField>

        <FormField
          id="hs_phone"
          label="Phone">
          <Input
            id="hs_phone"
            v-model="hsPhone"
            type="tel"
            placeholder="+33 6 12 34 56 78"
            class="text-sm" />
        </FormField>
      </div>
    </template>

    <template v-if="showRoleSections">
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium">Roles per account</span>
          <Button
            variant="outline"
            size="sm"
            @click="addGrant('Account')">
            <Plus class="size-4" />
            Add role
          </Button>
        </div>
        <p class="text-xs text-muted-foreground">
          A role on an account also applies to its sub-accounts. Removing the last role removes access to the account.
        </p>

        <div
          v-for="(grant, index) in accountGrants"
          :key="`account-${index}`"
          class="rounded-lg border border-border p-3">
          <div class="flex items-center gap-3">
            <Building2 class="size-4 shrink-0 text-foreground-subtle" />
            <Select
              :model-value="grant.roleable_id || undefined"
              :options="accountOptions"
              placeholder="Select an account"
              filterable
              class="flex-1"
              @update:model-value="updateGrant(grant, { roleable_id: Number($event) })" />
            <Select
              :model-value="grant.role_id || undefined"
              :options="roleOptions"
              placeholder="Select a role"
              class="w-40"
              @update:model-value="updateGrant(grant, { role_id: Number($event) })" />
            <Button
              variant="ghost"
              size="icon"
              class="size-8 text-foreground-subtle hover:text-status-error-strong"
              @click="removeGrant(grant)">
              <Trash2 class="size-4" />
            </Button>
          </div>
          <div
            v-if="grant.roleable_id && subAccountNames(grant.roleable_id).length"
            class="mt-2 flex items-center gap-1.5 pl-7 text-xs text-foreground-subtle">
            <CornerDownRight class="size-3" />
            Inherited by sub-accounts: {{ subAccountNames(grant.roleable_id).join(", ") }}
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium">Roles per survey</span>
          <Button
            variant="outline"
            size="sm"
            @click="addGrant('Survey')">
            <Plus class="size-4" />
            Add role
          </Button>
        </div>
        <p class="text-xs text-muted-foreground">Gives access to a single survey only, without joining its account.</p>

        <div
          v-for="(grant, index) in surveyGrants"
          :key="`survey-${index}`"
          class="flex items-center gap-3 rounded-lg border border-border p-3">
          <FileText class="size-4 shrink-0 text-foreground-subtle" />
          <Select
            v-bind="surveyOptionsBindings"
            :model-value="grant.roleable_id || undefined"
            placeholder="Select a survey"
            class="flex-1"
            @update:model-value="updateGrant(grant, { roleable_id: Number($event) })" />
          <Select
            :model-value="grant.role_id || undefined"
            :options="roleOptions"
            placeholder="Select a role"
            class="w-40"
            @update:model-value="updateGrant(grant, { role_id: Number($event) })" />
          <Button
            variant="ghost"
            size="icon"
            class="size-8 text-foreground-subtle hover:text-status-error-strong"
            @click="removeGrant(grant)">
            <Trash2 class="size-4" />
          </Button>
        </div>
      </div>

      <EmptyState
        v-if="rolesGrantAccess && !roleGrants.length"
        class="rounded-lg border border-dashed border-border-strong p-4 text-foreground-subtle"
        title="No role — this member has no access." />
    </template>

    <FormField
      v-if="isEditing && user.created_at"
      label="Created At"
      :readonly="true">
      <div class="text-sm text-muted-foreground">
        {{ formatDate(user.created_at) }}
      </div>
    </FormField>
  </FormDialog>
</template>
